import { z } from 'zod'
import { getRegionFromZipCode } from '@/data/regional-climate'
import { GRASS_CHARACTERISTICS } from '@/data/grass-characteristics'
import { GRASS_KEYS } from '@/lib/soil-test/types'
import { FERTILIZER_TYPES } from '@/data/fertilizer-types'
import { WEED_CHARACTERISTICS } from '@/data/weed-types'
import { WEED_AND_FEED_PRODUCTS } from '@/data/weed-and-feed-products'
import {
  SERVICE_LEAD_PROPERTY_TYPES,
  SERVICE_LEAD_SERVICE_TYPES,
  SERVICE_LEAD_TIMELINES,
  SERVICE_LEAD_YARD_SIZE_RANGES,
} from '@/data/service-quote-funnels'
import { normalizeUsPhone } from '@/lib/service-leads/phone'

export { normalizeUsPhone } from '@/lib/service-leads/phone'

const MSG_ZIP_INVALID = 'Please enter a valid 5-digit ZIP code'
const MSG_ZIP_UNKNOWN = 'ZIP code not recognized. Please check and try again.'
const MSG_PHONE_INVALID = 'Please enter a valid US phone number'
const MSG_SERVICE_REQUIRED = 'Please select a service'
const MSG_PROPERTY_REQUIRED = 'Please select a property type'
const MSG_TIMELINE_REQUIRED = 'Please select a timeline'
const MSG_YARD_SIZE_REQUIRED = 'Please select a yard size'
const MSG_CONSENT_REQUIRED = 'Please agree to be contacted about your quote request'
const MSG_GRASS_TYPE_REQUIRED = 'Please select a grass type'
const MSG_SEASON_REQUIRED = 'Please select current season'
const MSG_SOIL_TYPE_REQUIRED = 'Please select soil type'
const MSG_LAWN_SIZE_MIN = 'Lawn size must be at least 100 square feet'
const MSG_LAWN_SIZE_MAX = 'Lawn size cannot exceed 217,800 square feet (5 acres)'
const MSG_ZONE_COUNT_RANGE = 'Zone count must be between 1 and 24'
const MSG_CATCH_CAN_MINUTES_RANGE = 'Catch-can test length must be between 5 and 60 minutes'
const MSG_CATCH_CAN_DEPTH_RANGE = 'Catch-can depth must be between 0.01 and 3 inches'
const MSG_WEED_UNKNOWN = 'Please choose a recognized weed from the list'

const STANDARD_GRASS_TYPES = ['Not Sure', ...Object.keys(GRASS_CHARACTERISTICS)] as [string, ...string[]]
const SEEDING_GRASS_TYPES = [...STANDARD_GRASS_TYPES, 'Other/Not Listed'] as [string, ...string[]]
const FERTILIZER_TYPE_KEYS = Object.keys(FERTILIZER_TYPES) as [string, ...string[]]
const FERTILIZER_TYPE_OPTIONS = ['recommend', ...FERTILIZER_TYPE_KEYS] as [string, ...string[]]
const SEASONS = ['spring', 'summer', 'fall', 'winter'] as const
const SOIL_TYPES = ['clay', 'sandy', 'loamy', 'unknown'] as const

export const emailSchema = z.string().email('Invalid email address')
export const passwordSchema = z.string().min(8, 'Password must be at least 8 characters')

// RFC 2606 reserved + common test/junk domains. Subscriptions to these bounce
// 100% of the time and degrade sender reputation. Compared lowercase.
const RESERVED_EMAIL_DOMAINS = new Set([
  'example.com', 'example.org', 'example.net',
  'test.com', 'test.org', 'test.net',
  'localhost', 'invalid',
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'trashmail.com',
  '10minutemail.com', 'yopmail.com',
])

// Role addresses don't belong to a real person; they shouldn't be subscribed
// to a newsletter. Compared lowercase against the local-part.
const ROLE_ADDRESS_LOCAL_PARTS = new Set([
  'postmaster', 'abuse', 'hostmaster', 'webmaster',
  'noreply', 'no-reply', 'donotreply', 'do-not-reply',
  'admin', 'administrator', 'root',
])

const MSG_EMAIL_INVALID = 'Please enter a valid email address'
const MSG_EMAIL_RESERVED = 'Please use a real email address'
const MSG_EMAIL_ROLE = 'Please use a personal email address'

export const subscribeEmailSchema = z
  .string()
  .email(MSG_EMAIL_INVALID)
  .transform((v) => v.trim().toLowerCase())
  .refine(
    (v) => {
      const at = v.indexOf('@')
      if (at < 0) return true
      return !RESERVED_EMAIL_DOMAINS.has(v.slice(at + 1))
    },
    { error: MSG_EMAIL_RESERVED }
  )
  .refine(
    (v) => {
      const at = v.indexOf('@')
      if (at < 0) return true
      const local = v.slice(0, at).split('+')[0]
      return !ROLE_ADDRESS_LOCAL_PARTS.has(local)
    },
    { error: MSG_EMAIL_ROLE }
  )

const zipCode = z.string().regex(/^\d{5}$/, MSG_ZIP_INVALID)
const zipCodeKnownRegion = zipCode.refine(
  (zip) => getRegionFromZipCode(zip) !== 'Unknown',
  { error: MSG_ZIP_UNKNOWN }
)

const optionalSanitizedText = (maxLength: number) =>
  z
    .string()
    .trim()
    .max(maxLength, `Must be ${maxLength} characters or fewer`)
    .transform((value) => {
      const sanitized = sanitizeString(value)
      return sanitized.length > 0 ? sanitized.slice(0, maxLength) : undefined
    })
    .optional()

const lawnSize = z.number().min(100, MSG_LAWN_SIZE_MIN).max(217800, MSG_LAWN_SIZE_MAX)
const grassTypeEnum = z.enum(STANDARD_GRASS_TYPES, { error: MSG_GRASS_TYPE_REQUIRED })
const seasonEnum = z.enum(SEASONS, { error: MSG_SEASON_REQUIRED })
const soilTypeEnum = z.enum(SOIL_TYPES, { error: MSG_SOIL_TYPE_REQUIRED })

// Ground-truth verification of a scan result (PATCH /api/user/analysis/[id]).
// 'Not Sure' is excluded — an attestation must name a real grass type.
const MSG_VERIFIED_GRASS_INVALID = 'Please select a valid grass type'
export const verifyAnalysisSchema = z.object({
  verifiedGrassType: grassTypeEnum.refine((v) => v !== 'Not Sure', { error: MSG_VERIFIED_GRASS_INVALID }),
  source: z.enum(['confirmed', 'corrected', 'picker_fallback'], { error: 'Invalid verification source' }),
})

// Structural gates for the AI vision output (analyze-grass route). Loose on
// purpose: they only reject shapes that would corrupt results/persistence
// (e.g. {}), not extra fields — the strict contract lives in the OpenAI
// json_schema. Confidence bounds mirror that schema (0-100).
export const aiIdentifyResultSchema = z.looseObject({
  grassType: z.string().min(1),
  subjectGuess: z.enum(['grass', 'weed', 'other_plant', 'not_plant', 'unreadable']).optional(),
  abstentionReason: z
    .enum(['poor_image', 'insufficient_visual_evidence', 'unsupported_turfgrass', 'non_grass_subject'])
    .nullish(),
  confidence: z.number().min(0).max(100),
  // Optional (looser than the strict OpenAI schema, where both are
  // nullable-but-required): heuristic fallbacks and pre-2026-07 cached rescan
  // rows carry neither, and both must keep validating.
  candidates: z
    .array(z.looseObject({ name: z.string().min(1), probability: z.number().min(0).max(1) }))
    .nullish(),
  weedGuess: z.string().nullish(),
  // Optional for fallback payloads and cached rows created before the mixed
  // turf/weed contract shipped. The strict model schema requires the field.
  possibleWeeds: z
    .array(z.looseObject({
      name: z.string().min(1),
      confidence: z.number().min(40).max(100),
      evidence: z.string().min(1),
    }))
    .max(2)
    .optional(),
  visibleEvidence: z.array(z.string().min(1)).max(5).optional(),
})

// The diagnosis gate mirrors the strict Responses json_schema's required
// fields because the mobile diagnosis screen dereferences them directly
// (SEVERITY_CONFIG[problem.severity].label, problem.type.replace,
// problem.visualEvidence.length) — a Chat/fallback payload missing any of
// them must degrade to the heuristic path, not crash the client.
export const aiDiagnosisResultSchema = z.looseObject({
  mode: z.literal('diagnosis').optional(),
  outcome: z.enum(['healthy', 'problems', 'inconclusive']).optional(),
  problems: z.array(
    z.looseObject({
      name: z.string().min(1),
      conditionId: z.string().min(1).nullable().optional(),
      type: z.enum(['disease', 'pest', 'weed', 'nutrient_deficiency', 'environmental']),
      severity: z.enum(['mild', 'moderate', 'severe']),
      urgency: z.enum(['immediate', 'soon', 'monitor']),
      confidence: z.number().min(0).max(100),
      visualEvidence: z.array(z.string()),
      // Nullable in the strict schema. A malformed box (pixel coords from an
      // unconstrained fallback payload) must degrade to null — never fail the
      // whole diagnosis over an optional visual aid — hence .catch(null).
      // Optional because pre-2026-07 cached rows lack the field entirely.
      boundingBox: z
        .looseObject({
          x: z.number().min(0).max(1),
          y: z.number().min(0).max(1),
          width: z.number().min(0).max(1),
          height: z.number().min(0).max(1),
        })
        .nullish()
        .catch(null),
      // Required+nullable in fresh strict responses. Optional here so legacy
      // cached rows still render. Invalid fallback values become null rather
      // than placing evidence on the wrong photo.
      sourceImageIndex: z.number().int().min(0).nullable().optional().catch(null),
    })
  ),
  overallAssessment: z.string().min(1),
  immediateActions: z.array(z.string()),
  preventionTips: z.array(z.string()),
  imageQuality: z.enum(['Good', 'Fair', 'Poor']),
  confidence: z.number().min(0).max(100),
  region: z.string(),
})

export const aiWeedResultSchema = z.looseObject({
  mode: z.literal('weed'),
  outcome: z.enum(['supported', 'inconclusive', 'unsupported']),
  weedName: z.string().nullable(),
  confidence: z.number().min(0).max(100),
  candidates: z.array(z.looseObject({
    name: z.string().min(1),
    probability: z.number().min(0).max(1),
    evidence: z.string(),
  })).min(1).max(3),
  visualEvidence: z.array(z.string()).max(5),
  analysis: z.string(),
  imageQuality: z.enum(['Good', 'Fair', 'Poor']),
  abstentionReason: z.enum([
    'poor_image',
    'insufficient_visual_evidence',
    'low_candidate_margin',
    'unsupported_weed',
    'contradictory_evidence',
    'not_a_weed',
  ]).nullable(),
  missingEvidence: z.array(z.string()).max(3),
  recommendedNextPhoto: z.string().nullable(),
  // Free-text best guess, allowed outside the supported taxonomy. Optional with
  // a null fallback so responses and cached rows from before the field parse.
  bestGuessName: z.string().nullable().optional().catch(null),
  region: z.string(),
})

export const seedingCalculatorSchema = z.object({
  zipCode: zipCodeKnownRegion,
  grassType: z.enum(SEEDING_GRASS_TYPES, { error: MSG_GRASS_TYPE_REQUIRED }),
  planningDate: z.date().optional(),
})

export const mowingCalculatorSchema = z.object({
  zipCode,
  grassType: grassTypeEnum,
  season: seasonEnum.optional(),
})

export const fertilizerCalculatorSchema = z.object({
  zipCode: zipCode.optional(),
  lawnSize,
  grassType: grassTypeEnum,
  fertilizerType: z.enum(FERTILIZER_TYPE_OPTIONS, { error: 'Please select a fertilizer type' }),
  season: seasonEnum,
  applicationGoal: z
    .enum(['maintenance', 'establishment', 'recovery', 'seasonal'], { error: 'Please select application goal' })
    .optional(),
  soilType: soilTypeEnum.optional(),
})

export const aerationCalculatorSchema = z.object({
  zipCode: zipCodeKnownRegion,
  grassType: grassTypeEnum,
})

// Weed & feed uses its own compact grass-id set (not the site's display-name
// grass keys) so the herbicide-safety gate stays readable. Grass is optional:
// the tool warns rather than blocks when it is absent.
const WEED_FEED_GRASS_IDS = ['bermuda', 'zoysia', 'staug', 'centipede', 'fescue', 'kbg'] as const
const WEED_FEED_PRODUCT_IDS = new Set(WEED_AND_FEED_PRODUCTS.map((product) => product.id))
const MSG_PRODUCT_REQUIRED = 'Please choose a weed & feed product'

export const weedAndFeedCalculatorSchema = z.object({
  zipCode: zipCodeKnownRegion,
  productId: z.string()
    .min(1, { error: MSG_PRODUCT_REQUIRED })
    .refine((productId) => WEED_FEED_PRODUCT_IDS.has(productId), { error: MSG_PRODUCT_REQUIRED }),
  lawnSize,
  grassType: z.enum(WEED_FEED_GRASS_IDS, { error: MSG_GRASS_TYPE_REQUIRED }).optional(),
})

// Overseeding uses the same compact grass-id set as the calculator engine
// (lib/overseeding-calculator.ts OverseedGrassId), not the site's display-name
// grass keys.
const OVERSEED_GRASS_IDS = ['fescue', 'kbg', 'rye', 'fine', 'bermuda', 'zoysia', 'centipede', 'staug', 'annualrye', 'coolmix'] as const

export const overseedingCalculatorSchema = z.object({
  zipCode: zipCodeKnownRegion,
  grassType: z.enum(OVERSEED_GRASS_IDS, { error: MSG_GRASS_TYPE_REQUIRED }),
  lawnSize,
  seedType: z.enum(['coated', 'uncoated'], { error: 'Please choose coated or uncoated seed' }).optional(),
  lawnCondition: z.enum(['thin', 'patchy', 'mostly-bare'], { error: 'Please choose how your lawn looks' }).optional(),
})

// Grub control: strategy is preventive vs curative (or 'auto' = let the date and
// symptoms decide); symptomCount is how many of the 5 diagnostic signs are checked.
export const grubControlCalculatorSchema = z.object({
  zipCode: zipCodeKnownRegion,
  lawnSize,
  strategy: z.enum(['auto', 'preventive', 'curative'], { error: 'Please choose a strategy' }).optional(),
  symptomCount: z.number().int().min(0).max(5).optional(),
})

// Sod calculator uses the engine's compact sod-variety id set (see
// lib/sod/calculator.ts SOD_GRASSES). ZIP and grass are both optional: the tool
// falls back to national-average pricing / blended "not sure" band and skips the
// seasonal verdict when they are absent.
const SOD_GRASS_IDS = ['bermuda', 'zoysia', 'staug', 'centipede', 'fescue', 'kbg', 'notsure'] as const

export const sodCalculatorSchema = z.object({
  zipCode: zipCode.optional(),
  lawnSize,
  grassType: z.enum(SOD_GRASS_IDS, { error: MSG_GRASS_TYPE_REQUIRED }).optional(),
})

// Topsoil / topdressing volume calculator. ZIP optional (national-average
// pricing when absent). Material + depth drive the cubic-yards math; depth is
// capped at 2" to match the engine's fill ceiling (lib/top-dressing/calculator).
const TOPSOIL_MATERIAL_IDS = ['topsoil', 'sand', 'compost', 'soil_mix', 'peat'] as const

export const topsoilCalculatorSchema = z.object({
  zipCode: zipCode.optional(),
  lawnSize,
  material: z.enum(TOPSOIL_MATERIAL_IDS, { error: 'Please choose a material' }),
  targetDepth: z.number().min(0.125, 'Depth must be at least 1/8 inch').max(2, 'Depth cannot exceed 2 inches'),
})

export const wateringScheduleSchema = z.object({
  zipCode,
  grassType: grassTypeEnum,
  lawnSize,
  zoneCount: z
    .number()
    .refine(Number.isInteger, { error: MSG_ZONE_COUNT_RANGE })
    .min(1, MSG_ZONE_COUNT_RANGE)
    .max(24, MSG_ZONE_COUNT_RANGE)
    .optional(),
  catchCanTestMinutes: z
    .number()
    .min(5, MSG_CATCH_CAN_MINUTES_RANGE)
    .max(60, MSG_CATCH_CAN_MINUTES_RANGE)
    .optional(),
  catchCanDepthInches: z
    .number()
    .min(0.01, MSG_CATCH_CAN_DEPTH_RANGE)
    .max(3, MSG_CATCH_CAN_DEPTH_RANGE)
    .optional(),
  zoneCatchCanDepthsInches: z
    .array(z.number().min(0.01, MSG_CATCH_CAN_DEPTH_RANGE).max(3, MSG_CATCH_CAN_DEPTH_RANGE))
    .max(24, MSG_ZONE_COUNT_RANGE)
    .optional(),
  soilType: soilTypeEnum,
  irrigationSystem: z.string().min(1, 'Please select an irrigation system'),
  season: seasonEnum.optional(),
  waterRestrictions: z.boolean().optional(),
  costPerGallon: z
    .number()
    .min(0.001, 'Cost per gallon must be positive')
    .max(10, 'Cost per gallon seems too high')
    .optional(),
})

export const lawnCostEstimatorSchema = z.object({
  lawnSize,
  grassType: grassTypeEnum,
  zipCode: zipCodeKnownRegion,
  serviceLevel: z.enum(['basic', 'standard', 'premium'], { error: 'Please select service level' }),
  careApproach: z.enum(['diy', 'professional', 'hybrid'], { error: 'Please select care approach' }),
  includeEquipment: z.boolean().optional(),
  includeWater: z.boolean().optional(),
})

export const serviceLeadSubmitSchema = z.object({
  serviceType: z.enum(SERVICE_LEAD_SERVICE_TYPES, { error: MSG_SERVICE_REQUIRED }),
  zipCode: zipCodeKnownRegion,
  propertyType: z.enum(SERVICE_LEAD_PROPERTY_TYPES, { error: MSG_PROPERTY_REQUIRED }),
  timeline: z.enum(SERVICE_LEAD_TIMELINES, { error: MSG_TIMELINE_REQUIRED }),
  yardSizeRange: z.enum(SERVICE_LEAD_YARD_SIZE_RANGES, { error: MSG_YARD_SIZE_REQUIRED }),
  name: z
    .string()
    .trim()
    .min(2, 'Please enter your name')
    .max(100, 'Name must be 100 characters or fewer')
    .transform((value) => sanitizeString(value).slice(0, 100)),
  email: emailSchema.transform((value) => value.trim().toLowerCase()),
  phone: z
    .string()
    .trim()
    .min(1, 'Please enter your phone number')
    .transform(normalizeUsPhone)
    .refine((value) => /^\+1\d{10}$/.test(value), { error: MSG_PHONE_INVALID }),
  description: optionalSanitizedText(1000),
  photoUrl: z.string().url('Invalid photo URL').max(2048).optional(),
  photoThumbnailUrl: z.string().url('Invalid photo thumbnail URL').max(2048).optional(),
  sourcePage: optionalSanitizedText(500),
  sourcePlacement: optionalSanitizedText(120),
  landingPath: optionalSanitizedText(500),
  utmSource: optionalSanitizedText(120),
  utmMedium: optionalSanitizedText(120),
  utmCampaign: optionalSanitizedText(180),
  consentAccepted: z.literal(true, { error: MSG_CONSENT_REQUIRED }),
})

export type ServiceLeadSubmitInput = z.infer<typeof serviceLeadSubmitSchema>

export const herbicideTimingSchema = z.object({
  zipCode: zipCodeKnownRegion,
  grassType: grassTypeEnum,
  targetWeeds: z.array(
    z.string().refine((weed) => weed in WEED_CHARACTERISTICS, { error: MSG_WEED_UNKNOWN })
  ),
  applicationType: z.enum(['pre-emergent', 'post-emergent', 'both'], { error: 'Please select an application type' }),
})

export const openaiTextCompletionSchema = z.object({
  prompt: z.string().min(1, 'Prompt is required').max(4000, 'Prompt too long'),
  maxTokens: z.number().min(1).max(4000).optional().default(150),
})

export const openaiImageAnalysisSchema = z.object({
  imageUrl: z.string().url('Invalid image URL'),
  prompt: z.string().min(1).max(1000).optional().default('What type of grass is this?'),
})

export const stripeCustomerSchema = z.object({
  email: emailSchema,
  name: z.string().min(1).max(100).optional(),
  metadata: z.record(z.string(), z.string()).optional(),
})

export const stripePaymentIntentSchema = z.object({
  amount: z.number().min(50, 'Minimum amount is $0.50').max(999999, 'Amount too high'),
  currency: z.string().length(3).optional().default('usd'),
  metadata: z.record(z.string(), z.string()).optional(),
})

export const quizSubmitSchema = z.object({
  email: emailSchema,
  zipCode,
  grassType: z.string().optional(),
  lawnSize,
  sunlight: z.enum(['full', 'partial', 'shade'], { error: 'Please select a sunlight level' }),
  season: seasonEnum,
  imageUrl: z.string().url('Invalid image URL').optional(),
})

export const supabaseAuthSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
})

// ---------------------------------------------------------------------------
// Soil test PDF analyzer
// ---------------------------------------------------------------------------

const MSG_SOIL_TEST_GRASS_REQUIRED = 'Select a grass type so we can interpret values for your lawn'
const MSG_SOIL_TEST_PH_RANGE = 'pH must be between 3 and 10'
const MSG_SOIL_TEST_PCT_RANGE = 'Percent values must be between 0 and 100'
const MSG_SOIL_TEST_CEC_RANGE = 'CEC must be between 1 and 50 meq/100g'
const MSG_SOIL_TEST_NUTRIENT_RANGE = 'Nutrient values must be between 0 and 5000 ppm'

// Context fields the API route accepts alongside the PDF upload. Note that
// the file itself comes via multipart/form-data and is validated by the
// upload-hardening layer (magic-byte check + size cap), not by Zod.
export const soilTestParseContextSchema = z.object({
  grassType: z.enum(STANDARD_GRASS_TYPES, { error: MSG_SOIL_TEST_GRASS_REQUIRED }),
  zipCode: zipCode.optional(),
  lawnSize: lawnSize.optional(),
  lawnId: z.string().uuid().optional(),
})

const SOIL_LAB_NAMES = ['logan_labs', 'waypoint', 'spectrum', 'al_eastern', 'umass', 'other'] as const

// Manual entry: user types values directly into a form when their lab is
// unsupported or the PDF parse failed. Only pH is required; other fields
// flow through as null when omitted.
export const soilTestManualEntrySchema = z.object({
  grassType: z.enum(STANDARD_GRASS_TYPES, { error: MSG_SOIL_TEST_GRASS_REQUIRED }),
  zipCode: zipCode.optional(),
  lawnSize: lawnSize.optional(),
  lawnId: z.string().uuid().optional(),
  labName: z.enum(SOIL_LAB_NAMES).optional(),
  testDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').optional(),
  ph: z.number().min(3, MSG_SOIL_TEST_PH_RANGE).max(10, MSG_SOIL_TEST_PH_RANGE),
  organicMatterPct: z.number().min(0, MSG_SOIL_TEST_PCT_RANGE).max(15, MSG_SOIL_TEST_PCT_RANGE).nullable().optional(),
  cec: z.number().min(1, MSG_SOIL_TEST_CEC_RANGE).max(50, MSG_SOIL_TEST_CEC_RANGE).nullable().optional(),
  phosphorusPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  potassiumPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  calciumPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  magnesiumPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  sulfurPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  ironPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  manganesePpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  zincPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  boronPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
  copperPpm: z.number().min(0).max(5000, MSG_SOIL_TEST_NUTRIENT_RANGE).nullable().optional(),
})

// Used by the claim route to attach an anonymous soil test to an authed user
// (and optionally to a saved lawn).
export const soilTestSaveSchema = z.object({
  claimToken: z.string().uuid('Invalid claim token'),
  lawnId: z.string().uuid().optional(),
})

// ---------------------------------------------------------------------------
// Lime / soil pH calculator (/tools/lime-calculator)
// ---------------------------------------------------------------------------

const MSG_LIME_PH_RANGE = 'Enter a soil pH between 3 and 10'
const MSG_LIME_GRASS_REQUIRED = 'Select a grass type so we can set your target pH'
const MSG_LIME_TEXTURE_REQUIRED = 'Select your soil texture'

const LIME_SOIL_TEXTURES = ['sandy', 'loam', 'clay'] as const
const LIME_MAGNESIUM_LEVELS = ['adequate', 'low', 'unknown'] as const

// grassType speaks the soil-test engine's canonical GrassKey vocabulary (the lime
// tool reuses that engine), not the display-name STANDARD_GRASS_TYPES used elsewhere.
export const limeCalculatorSchema = z.object({
  currentPh: z.number().min(3, MSG_LIME_PH_RANGE).max(10, MSG_LIME_PH_RANGE),
  grassType: z.enum(GRASS_KEYS as unknown as [string, ...string[]], {
    error: MSG_LIME_GRASS_REQUIRED,
  }),
  soilTexture: z.enum(LIME_SOIL_TEXTURES, { error: MSG_LIME_TEXTURE_REQUIRED }),
  lawnSize,
  magnesium: z.enum(LIME_MAGNESIUM_LEVELS).optional(),
  zipCode: zipCode.optional(),
})

export function validateInput<T>(
  schema: z.ZodType<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: string } {
  try {
    const validated = schema.parse(data)
    return { success: true, data: validated }
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { success: false, error: error.issues[0]?.message || 'Validation failed' }
    }
    return { success: false, error: 'Invalid input' }
  }
}

const MSG_GRASS_CATEGORY_INVALID = 'Grass category must be auto, cool, or warm'

/** ZIP threshold email alerts (soil temperature map). */
export const zipAlertSubscribeSchema = z.object({
  email: subscribeEmailSchema,
  zipCode: zipCodeKnownRegion,
  grassCategory: z
    .enum(['auto', 'cool', 'warm'], { error: MSG_GRASS_CATEGORY_INVALID })
    .optional(),
})

export function sanitizeString(input: string): string {
  return input.trim().replace(/[<>]/g, '').substring(0, 1000)
}

export function sanitizeEmail(email: string): string {
  return email.toLowerCase().trim()
}
